import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import type { IScheduledPdfCustomReport } from '@/Interfaces/PdfBuilder/ScheduledPdfCustomReport.interface';
import type { ICustomReport } from '@/Interfaces/ReportBuilder/CustomReport.interface';
import type { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { getSortFilter } from '@/helpers/grid/getSortFilter';
import { fetchPdfReports, fetchScheduledPdfReports } from '@/store/api/pdfBuilder.api';
import { pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import type { RequestId } from '@/types/Slice.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface IState {
  generatedReports: ICustomReport[];
  generatedReportsTotal: number;
  generatedReportsRequestId: RequestId;
  scheduledReports: IScheduledPdfCustomReport[];
  scheduledReportsTotal: number;
  scheduledReportsRequestId: RequestId;
}

enum ActionPrefix {
  GetGeneratedReports = 'pdfBuilderSlice/getGeneratedReports',
  GetScheduledReports = 'pdfBuilderSlice/getScheduledReports',
}

const initialState: IState = {
  generatedReports: [],
  generatedReportsTotal: 0,
  generatedReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  scheduledReports: [],
  scheduledReportsTotal: 0,
  scheduledReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
};

export const pdfBuilderSlice = createSlice({
  name: 'pdfBuilder',
  initialState,
  reducers: {
    resetPdfBuilderState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getGeneratedReports.pending, pendingCaseHandler('generatedReports'))
      .addCase(getGeneratedReports.rejected, rejectedCaseHandler('generatedReports'))
      .addCase(getGeneratedReports.fulfilled, (state, action) => {
        if (state.generatedReportsRequestId === action.meta.requestId) {
          state.generatedReportsRequestId = null;
          state.generatedReports = action.payload?.data ?? [];
          state.generatedReportsTotal = action.payload?.meta?.total ?? 0;
        }
      })
      .addCase(getScheduledReports.pending, pendingCaseHandler('scheduledReports'))
      .addCase(getScheduledReports.rejected, rejectedCaseHandler('scheduledReports'))
      .addCase(getScheduledReports.fulfilled, (state, action) => {
        if (state.scheduledReportsRequestId === action.meta.requestId) {
          state.scheduledReportsRequestId = null;
          state.scheduledReports = action.payload?.data ?? [];
          state.scheduledReportsTotal = action.payload?.meta?.total ?? 0;
        }
      });
  },
});

export const { resetPdfBuilderState } = pdfBuilderSlice.actions;

export const getGeneratedReports = createAsyncThunk<
  IPaginatedApiResponse<ICustomReport[]>,
  Partial<ICustomReportListRequestPayload> & IAbortSignalThunkArg
>(ActionPrefix.GetGeneratedReports, async payload => {
  const { pageSize, page, sort, signal } = payload ?? {};
  const params = {
    limit: pageSize ?? 15,
    page: page ?? 1,
    sort: getSortFilter(sort),
  };

  return await fetchPdfReports(params, signal);
});

export const getScheduledReports = createAsyncThunk<
  IPaginatedApiResponse<IScheduledPdfCustomReport[]>,
  Partial<ICustomReportListRequestPayload> & IAbortSignalThunkArg
>(ActionPrefix.GetScheduledReports, async payload => {
  const { pageSize, page, sort, signal } = payload ?? {};
  const params = {
    limit: pageSize ?? 15,
    page: page ?? 1,
    sort: getSortFilter(sort),
  };

  return await fetchScheduledPdfReports(params, signal);
});

export default pdfBuilderSlice.reducer;
